import { Panel } from "@/components/home/panel";
import { TierChip } from "@/components/tiers";

export interface ScoreLine {
  key: string;
  count: number;
  points: number;
}

export interface HistoryRow {
  period_month: string;
  label: string;
  rank: number;
  of: number;
  score: number;
  tier: string;
}

export interface Badge {
  kind: string;
  level: number;
  period_month: string;
  detail: Record<string, unknown>;
}

interface Standing {
  rank: number;
  of: number;
  score: number;
  components: ScoreLine[];
  behind_next: number | null;
  next_rank: number | null;
  per_active_day: number;
  days_active: number;
  listings: number;
  streak: number;
  fx_complete: boolean;
  tier: string;
  next_tier: string | null;
  to_next_tier: number | null;
}

export const BADGE_META: Record<string, { icon: string; label: string; hint: string }> = {
  champion: { icon: "🏆", label: "Champion", hint: "Finished first" },
  runner_up: { icon: "🥈", label: "Runner-up", hint: "Finished second" },
  third: { icon: "🥉", label: "Third place", hint: "Finished third" },
  thousand_club: { icon: "📈", label: "Thousand club", hint: "1,000 listings in a month" },
  double_up: { icon: "🚀", label: "Double up", hint: "2,000 listings in a month" },
  clean_month: { icon: "✨", label: "Clean month", hint: "Under 2% taken down" },
  first_deal: { icon: "🎉", label: "First deal", hint: "Closed your first deal" },
};

/** Each credit gets its own shade — a single-colour bar would be one solid
 *  block and say nothing about what the score is made of. */
const BAR: Record<string, string> = {
  listings: "bg-accent-500",
  goal_days: "bg-sky-400",
  deals: "bg-emerald-400",
  commission: "bg-amber-400",
};

/** All score copy lives here. The API ships `{key, count, points}` and nothing
 *  else, so the wording never forks between Python and TypeScript. */
const LINE: Record<string, { label: string; unit: (n: number) => string }> = {
  listings: { label: "Listings published", unit: (n) => n.toLocaleString() },
  goal_days: { label: "Days you hit the goal", unit: (n) => `${n}` },
  deals: { label: "Deals closed", unit: (n) => `${n}` },
  commission: { label: "Commission", unit: (n) => `₾${n.toLocaleString()}` },
  removed: { label: "Listings later taken down", unit: (n) => n.toLocaleString() },
  failed: { label: "Failed publishes", unit: (n) => n.toLocaleString() },
};

/** The answer to "why am I 4th".
 *
 *  A blended score is only defensible if every point is traceable, so this is
 *  a hard requirement of the feature rather than a nice extra — and it doubles
 *  as coaching, since a broker can see exactly what their removals cost.
 */
export function ScoreBreakdown({
  me,
  periodLabel,
  goalDaily,
}: {
  me: Standing;
  periodLabel: string;
  goalDaily: number;
}) {
  const credits = me.components.filter((c) => c.points > 0);
  const penalties = me.components.filter((c) => c.points < 0);
  const creditTotal = credits.reduce((a, c) => a + c.points, 0);

  return (
    <Panel
      title={`Your ${periodLabel} score`}
      right={
        <span className="tabular-nums">
          #{me.rank} of {me.of}
        </span>
      }
    >
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums leading-none text-slate-100">
          {me.score.toLocaleString()}
        </span>
        <span className="text-[11px] text-slate-500">points</span>
        <TierChip tier={me.tier} />
        {me.streak > 1 && (
          <span className="badge text-[10px] border-amber-500/30 bg-amber-500/10 text-amber-300 ml-1">
            🔥 {me.streak}-day streak
          </span>
        )}
      </div>

      {/* credits vs penalties, to scale */}
      {creditTotal > 0 && (
        <div className="mt-3 flex h-1.5 rounded-full overflow-hidden bg-white/[0.06]">
          {credits.map((c) => (
            <div
              key={c.key}
              className={BAR[c.key] ?? "bg-slate-500"}
              style={{ width: `${(c.points / creditTotal) * 100}%` }}
              title={`${LINE[c.key]?.label ?? c.key}: +${c.points}`}
            />
          ))}
        </div>
      )}

      <table className="w-full text-[12px] mt-3">
        <tbody>
          {[...credits, ...penalties].map((c) => {
            const meta = LINE[c.key] ?? { label: c.key, unit: String };
            return (
              <tr key={c.key} className="border-b border-white/[0.04] last:border-0">
                <td className="py-1 text-slate-300">{meta.label}</td>
                <td className="py-1 text-right tabular-nums text-slate-500 pr-3">
                  {meta.unit(c.count)}
                </td>
                <td
                  className={`py-1 text-right tabular-nums w-[68px] ${
                    c.points < 0 ? "text-rose-300" : "text-emerald-300"
                  }`}
                >
                  {c.points > 0 ? "+" : ""}
                  {c.points.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* the actionable part: what closes the gap */}
      {me.behind_next != null && me.next_rank != null && (
        <div className="mt-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
          <div className="text-[12px] text-slate-200">
            {me.behind_next.toLocaleString()} points behind {ordinal(me.next_rank)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            That's {me.behind_next.toLocaleString()} more listings, or{" "}
            {Math.ceil(me.behind_next / 200)} closed deal
            {Math.ceil(me.behind_next / 200) === 1 ? "" : "s"}, or{" "}
            {Math.ceil(me.behind_next / 10)} more days at the {goalDaily}/day goal.
          </div>
        </div>
      )}

      <div className="mt-2 text-[11px] text-slate-500">
        {me.days_active} active {me.days_active === 1 ? "day" : "days"} ·{" "}
        {me.per_active_day.toFixed(0)} listings a day
        {!me.fx_complete && (
          <span className="text-amber-300">
            {" "}
            · some commission has no FX rate, so this understates
          </span>
        )}
      </div>
    </Panel>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Badges earned, grouped by kind.
 *
 *  Two "Clean month" rows read as a rendering bug rather than as two months of
 *  clean work, so repeats collapse into a count and the months move into the
 *  subtitle.
 */
export function BadgeShelf({
  badges,
  showUnearned = false,
}: {
  badges: Badge[];
  showUnearned?: boolean;
}) {
  const groups = new Map<string, string[]>();
  for (const b of badges) {
    const at = new Date(b.period_month).toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
    groups.set(b.kind, [...(groups.get(b.kind) ?? []), at]);
  }
  const kinds = showUnearned
    ? Object.keys(BADGE_META)
    : [...groups.keys()].sort(
        (a, b) => Object.keys(BADGE_META).indexOf(a) - Object.keys(BADGE_META).indexOf(b),
      );

  return (
    <div className="divide-y divide-white/[0.04]">
      {kinds.map((kind) => {
        const meta = BADGE_META[kind] ?? { icon: "🎖", label: kind, hint: "" };
        const at = groups.get(kind);
        const earned = !!at?.length;
        return (
          <div
            key={kind}
            className={`flex items-center gap-2.5 px-4 py-2 ${earned ? "" : "opacity-40"}`}
          >
            <span
              className="text-base leading-none"
              style={earned ? undefined : { filter: "grayscale(1)" }}
            >
              {meta.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] text-slate-200 truncate">
                {meta.label}
                {at && at.length > 1 && (
                  <span className="text-slate-500 font-normal"> ×{at.length}</span>
                )}
              </span>
              <span className="block text-[10.5px] text-slate-500 truncate">
                {earned ? at!.join(" · ") : meta.hint}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The viewer's own finish in each sealed month — the progression a single
 *  live rank cannot show. */
export function HistoryPanel({ rows }: { rows: HistoryRow[] }) {
  // "Best" ignores months where you scored nothing. Ranks tie at zero, so the
  // agency's first month — where ten of eleven people scored 0 — hands out a
  // shared 2nd place that reads like a podium finish and would otherwise win
  // this highlight outright.
  const scored = rows.filter((r) => r.score > 0);
  const best = scored.length ? Math.min(...scored.map((r) => r.rank)) : null;
  return (
    <Panel title="Your past months" flush>
      <div className="divide-y divide-white/[0.04]">
        {rows.map((r) => (
          <div key={r.period_month} className="flex items-center gap-3 px-4 py-1.5">
            <span className="text-[12px] text-slate-400 w-[68px] shrink-0">{r.label}</span>
            <span
              className={`text-[12.5px] tabular-nums ${
                best !== null && r.rank === best && r.score > 0
                  ? "text-amber-300"
                  : "text-slate-200"
              }`}
            >
              #{r.rank}
              <span className="text-slate-500"> of {r.of}</span>
            </span>
            <span className="ml-auto flex items-center gap-2">
              <TierChip tier={r.tier} bare />
              <span className="text-[12px] tabular-nums text-slate-400 w-[72px] text-right">
                {r.score.toLocaleString()} pts
              </span>
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
